/**
 * Device enrollment.
 *
 * The code identifies which employee this phone belongs to. It is not a
 * credential — the dashboard validates it on every request, and Access plus
 * SYNC_TOKEN (held server-side in the sync proxy) are what actually grant
 * access. Keeping real credentials off phones is the entire point of the
 * proxy design, and this must not undo it.
 */

import { db } from './db';
// Namespace import on purpose: the handshake test asserts the ORDER of sync
// vs. store, which needs a working spy. A named import binds at module load
// and the spy would not be observed.
import * as syncService from './syncService';

const ENROLL_PATH = '/api/sync/enroll';

export interface EnrollmentOutcome {
  ok: boolean;
  employeeName?: string;
  employeeEmail?: string | null;
  error?: string;
}

const CONNECTION_ERROR = 'Could not connect. Check your signal and try again.';

/** Ask the dashboard who this code belongs to. Stores nothing. */
export async function validateEnrollmentCode(code: string): Promise<EnrollmentOutcome> {
  const trimmed = (code || '').trim();
  if (!trimmed) return { ok: false, error: 'Enter the code from the office.' };

  let response: Response;
  try {
    response = await fetch(ENROLL_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: trimmed }),
    });
  } catch (err) {
    // A network failure is NOT a bad code. Saying so would send an employee
    // chasing the office over a dropped signal.
    return { ok: false, error: CONNECTION_ERROR };
  }

  const body: any = await response.json().catch(() => null);

  if (response.ok) {
    return { ok: true, employeeName: body?.employeeName, employeeEmail: body?.employeeEmail ?? null };
  }

  // Only the discriminators the server actually promises for a genuine code
  // rejection (Task 5's contract) count as "your code is wrong." Everything
  // else — a 502/500 from the proxy, a non-JSON body, an unrecognized
  // discriminator — is a transport/connectivity problem, not evidence the
  // code itself is bad, and reporting it as one sends an employee chasing a
  // typo that doesn't exist. The proxy really does surface failures this
  // way: 502 {error:'Could not reach the dashboard'}, 500 {error:'Sync proxy
  // is not configured'}.
  const discriminator = body?.code;
  if (discriminator === 'enrollment_invalid' || discriminator === 'enrollment_revoked') {
    return {
      ok: false,
      error: body?.error || 'That code was not recognized. Check it and try again.',
    };
  }

  return { ok: false, error: CONNECTION_ERROR };
}

/**
 * The enrollment handshake. Order matters and is not stylistic:
 *
 *   validate -> reconcile outstanding entries under the OLD name-based
 *   attribution -> only THEN store the code.
 *
 * Storing the code first would make the next sync re-send this device's whole
 * history under the newly authoritative employee id. Those rows would match
 * nothing already in D1 and would be inserted as a second complete copy,
 * corrupting payroll and billing totals. See the spec's §5.2.
 *
 * There is deliberately no "mark everything synced" step. syncTimeEntries
 * stamps exactly the entries it delivered, with the timestamp the request was
 * SENT — that is already precisely the set that has been reconciled. A blanket
 * db.markAllEntriesSynced() on top of it was not just redundant, it was
 * destructive: it stamped every row with `new Date()` at call time, which
 * (a) overwrote the correct pre-request stamps and defeated the guarantee that
 * an edit landing mid-flight stays re-sendable, (b) stamped rows the server had
 * REJECTED, and (c) stamped shifts created DURING the reconciliation — a
 * clock-out mid-handshake calls autoSync, which returns immediately because
 * syncInProgress is set, so that shift is never sent, then gets stamped, then
 * is never re-sent. A whole shift disappears.
 */
export async function completeEnrollment(code: string): Promise<EnrollmentOutcome> {
  // Normalized once, up front, so the value used for validation, for the
  // reconciliation batch's device-code header, and for storage cannot drift
  // apart from one another.
  const normalizedCode = code.trim().toUpperCase();

  const validation = await validateEnrollmentCode(normalizedCode);
  if (!validation.ok) return validation;

  const outstanding = await db.getUnsyncedEntries();

  if (outstanding.length > 0) {
    // db.getEnrollmentCode() is still null at this point in the handshake —
    // the code is deliberately not stored until after reconciliation
    // succeeds (see the block comment above). So the code has to be passed
    // explicitly here, or this batch would go out with no x-device-code
    // header and would be rejected once server-side enforcement is switched
    // on. The preEnrollment flag tells the dashboard to attribute it the old
    // (name-based) way regardless.
    const result = await syncService.syncTimeEntries(outstanding, {
      preEnrollment: true,
      deviceCode: normalizedCode,
    });
    if (!result.success) {
      // Surface the real reason (sync disabled in Settings, a concurrent
      // autoSync already in progress, a 403 during reconciliation, ...)
      // rather than collapsing every failure into "check your signal" — that
      // message is actively misleading for e.g. "Sync is disabled", where
      // there is no connectivity problem to retry and enrollment is
      // impossible until the employee is told what to actually go fix.
      return {
        ok: false,
        error: result.message || 'Could not send your existing entries. Check your signal and try again.',
      };
    }
  }

  try {
    // Kept inside a try/catch, unlike validateEnrollmentCode's fetch: an
    // IndexedDB failure here must still resolve to an EnrollmentOutcome, not
    // reject the promise — Task 13's UI is written against the
    // { ok, employeeName?, error? } contract, not a thrown exception.
    await db.setEnrollmentCode(normalizedCode);
  } catch (err) {
    return {
      ok: false,
      error: 'Could not finish setting up this device. Try again.',
    };
  }

  return { ok: true, employeeName: validation.employeeName, employeeEmail: validation.employeeEmail };
}

export async function isEnrolled(): Promise<boolean> {
  return (await db.getEnrollmentCode()) !== null;
}

export async function clearEnrollment(): Promise<void> {
  await db.clearEnrollmentCode();
}
